// Insights automáticos (seção 7.3): cada card mostra a regra, os números que dispararam o alerta
// e o link para o extrato do produto; ordenados por impacto financeiro (R$). Cada insight pode
// ser marcado como "visto" ou "tratado" (com comentário). Regras: backend/services/estoque/insights/regras.py.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, AlertTriangle, CheckCircle2, Eye, History, Info, RotateCcw } from "lucide-react";
import { getEstoqueInsights, putEstoqueInsightStatus } from "../../api";
import { formatData, limitesMesISO, mensagemErro } from "./comum";
import { AvisoErro, Campo, Modal, btnStyle, formatBRL, inputStyle } from "./ui";

const SEVERIDADE = {
  alta: { rotulo: "Alta", Icone: AlertOctagon, cor: (C) => C.danger },
  media: { rotulo: "Média", Icone: AlertTriangle, cor: (C) => C.warning },
  baixa: { rotulo: "Baixa", Icone: Info, cor: (C) => C.muted },
};
const STATUS = { NOVO: "Novo", VISTO: "Visto", TRATADO: "Tratado" };

function CardInsight({ C, ins, ano, mes, onAlterado }) {
  const navigate = useNavigate();
  const [modalTratar, setModalTratar] = useState(false);
  const [comentario, setComentario] = useState(ins.comentario || "");
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const sev = SEVERIDADE[ins.severidade];
  const cor = sev.cor(C);
  const [inicio, fim] = limitesMesISO(ano, mes);

  async function marcar(status, texto = null) {
    setSalvando(true);
    setErro(null);
    try {
      await putEstoqueInsightStatus({ ano, mes, regra: ins.regra, produto_id: ins.produto_id, status, comentario: texto });
      setModalTratar(false);
      onAlterado();
    } catch (e) {
      setErro(e);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <article style={{ background: C.card, border: `1px solid ${C.border}`, borderLeft: `4px solid ${cor}`, borderRadius: 12, padding: 14, opacity: ins.status === "TRATADO" ? 0.7 : 1 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 240px", minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: cor }}>
              <sev.Icone size={14} /> {sev.rotulo}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>{ins.titulo}</span>
            {ins.status !== "NOVO" && (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, padding: "1px 8px", borderRadius: 999, background: C.bgActive, color: ins.status === "TRATADO" ? C.success : C.brand }}>
                {ins.status === "TRATADO" ? <CheckCircle2 size={12} /> : <Eye size={12} />} {STATUS[ins.status]}
              </span>
            )}
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4, color: C.text }}>{ins.mensagem}</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{ins.produto_nome} · {ins.categoria}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Impacto</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{formatBRL(ins.impacto)}</div>
        </div>
      </div>

      <dl style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "6px 16px", margin: "10px 0", fontSize: 12 }}>
        {Object.entries(ins.numeros).map(([k, v]) => (
          <div key={k}>
            <dt style={{ color: C.muted }}>{k}</dt>
            <dd style={{ margin: 0, fontWeight: 700, color: C.text }}>{v}</dd>
          </div>
        ))}
      </dl>
      <div style={{ fontSize: 12, color: C.muted, background: C.bgActive, borderRadius: 8, padding: "6px 10px" }}>
        <strong>Regra:</strong> {ins.regra_texto}
      </div>
      {ins.comentario && (
        <div style={{ fontSize: 12, marginTop: 8, color: C.text }}>
          <strong>{ins.status === "TRATADO" ? "Tratamento" : "Comentário"}:</strong> {ins.comentario}
          <span style={{ color: C.muted }}> — {ins.status_por_nome || "—"}, {formatData(ins.status_em)}</span>
        </div>
      )}
      <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
      <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
        <button onClick={() => navigate(`/estoque/kardex?produto=${ins.produto_id}&inicio=${inicio}&fim=${fim}`)} style={{ ...btnStyle(C.brand, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}>
          <History size={13} /> Ver extrato
        </button>
        {ins.status === "NOVO" && (
          <button disabled={salvando} onClick={() => marcar("VISTO")} style={{ ...btnStyle(C.muted, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}><Eye size={13} /> Marcar como visto</button>
        )}
        {ins.status !== "TRATADO" && (
          <button disabled={salvando} onClick={() => setModalTratar(true)} style={{ ...btnStyle(C.success, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}><CheckCircle2 size={13} /> Tratado</button>
        )}
        {ins.status !== "NOVO" && (
          <button disabled={salvando} onClick={() => marcar(null)} style={{ ...btnStyle(C.muted, true), padding: "6px 10px", minHeight: 34, fontSize: 12 }}><RotateCcw size={13} /> Reabrir</button>
        )}
      </div>

      {modalTratar && (
        <Modal C={C} titulo="Marcar como tratado" onClose={() => setModalTratar(false)} largura={460}
          rodape={<>
            <button onClick={() => setModalTratar(false)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button disabled={salvando || !comentario.trim()} onClick={() => marcar("TRATADO", comentario.trim())} style={{ ...btnStyle(C.success), opacity: comentario.trim() ? 1 : 0.5 }}>Salvar</button>
          </>}>
          <p style={{ marginTop: 0, fontSize: 13, color: C.muted }}>{ins.mensagem}</p>
          <Campo label="O que foi feito" C={C} obrigatorio>
            <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3} placeholder="Ex.: mínimo revisto para 10; próxima compra antecipada" style={{ ...inputStyle(C), resize: "vertical" }} autoFocus />
          </Campo>
        </Modal>
      )}
    </article>
  );
}

export default function InsightsEstoque({ C, ano, mes, isMobile }) {
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState("");
  const [regra, setRegra] = useState("");
  const [status, setStatus] = useState("pendentes");

  const carregar = useCallback(() => getEstoqueInsights(ano, mes).then(setDados).catch((e) => setErro(e.message)), [ano, mes]);
  useEffect(() => { carregar(); }, [carregar]);

  const itens = useMemo(() => (dados?.itens || []).filter((i) =>
    (!regra || i.regra === regra) &&
    (status === "todos" || (status === "pendentes" ? i.status !== "TRATADO" : i.status === status))
  ), [dados, regra, status]);

  if (erro) return <AvisoErro C={C}>{erro}</AvisoErro>;
  if (!dados) return <div style={{ color: C.muted, fontSize: 13 }}>Calculando insights...</div>;
  const r = dados.resumo;
  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: C.muted, flex: "1 1 260px" }}>
          <strong style={{ color: C.text }}>{r.total}</strong> insight(s) · <strong style={{ color: C.text }}>{r.novos}</strong> novo(s) · impacto somado <strong style={{ color: C.text }}>{formatBRL(r.impacto_total)}</strong>
          <div style={{ fontSize: 11, marginTop: 2 }}>Limites configuráveis em Parâmetros.</div>
        </div>
        <div style={{ flex: "0 1 230px" }}>
          <Campo label="Regra" C={C}>
            <select value={regra} onChange={(e) => setRegra(e.target.value)} style={inputStyle(C)}>
              <option value="">Todas ({r.total})</option>
              {r.por_regra.map((p) => <option key={p.regra} value={p.regra}>{p.titulo} ({p.quantidade})</option>)}
            </select>
          </Campo>
        </div>
        <div style={{ flex: "0 1 160px" }}>
          <Campo label="Situação" C={C}>
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={inputStyle(C)}>
              <option value="pendentes">Não tratados</option>
              <option value="NOVO">Novos</option>
              <option value="VISTO">Vistos</option>
              <option value="TRATADO">Tratados</option>
              <option value="todos">Todos</option>
            </select>
          </Campo>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {itens.length === 0 && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, textAlign: "center", color: C.muted, fontSize: 13 }}>
            {r.total === 0 ? "Nenhum alerta neste período." : "Nenhum insight com esse filtro."}
          </div>
        )}
        {itens.map((ins) => <CardInsight key={`${ins.regra}-${ins.produto_id}`} C={C} ins={ins} ano={ano} mes={mes} isMobile={isMobile} onAlterado={carregar} />)}
      </div>
    </div>
  );
}
